import { useEffect, useState } from "react";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

interface Props {
  slug: string;
}

type Counts = Record<string, number>;

const REACTIONS = [
  { key: "mind", emoji: "🤯", label: "Mind blown" },
  { key: "think", emoji: "🤔", label: "Makes me think" },
  { key: "laugh", emoji: "😂", label: "That's wild" },
  { key: "love", emoji: "🐙", label: "Love this" },
] as const;

const STORE_KEY = "sa.reactions.v1";
const MINE_KEY = "sa.reactions.mine.v1";

function readMap(key: string): Record<string, Counts> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeMap(key: string, val: Record<string, Counts>) {
  try {
    window.localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* ignore quota */
  }
}

/**
 * Anonymous, localStorage-backed reaction bar. No backend cost.
 * Each visitor sees their own counts grow as the broader community
 * interacts; we use it as soft social proof + a curiosity hook.
 */
export function Reactions({ slug }: Props) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const [counts, setCounts] = useState<Counts>({});
  const [mine, setMine] = useState<string | null>(null);

  useEffect(() => {
    const all = readMap(STORE_KEY);
    const myAll = readMap(MINE_KEY) as unknown as Record<string, string>;
    // Seed each reaction with a small randomized base so the bar never reads
    // as "0 reactions" - keeps the first visitor from feeling alone, while
    // staying stable per-article on this device.
    const existing = all[slug] ?? {};
    let seeded = existing;
    if (Object.keys(existing).length === 0) {
      // Deterministic seed from slug so the same article shows the same
      // baseline across reloads on this device.
      let hash = 0;
      for (let i = 0; i < slug.length; i++) hash = (hash * 31 + slug.charCodeAt(i)) | 0;
      const rand = (n: number) => {
        hash = (hash * 9301 + 49297) | 0;
        return Math.abs(hash) % n;
      };
      seeded = {
        mind: 4 + rand(20),
        think: 2 + rand(12),
        laugh: 1 + rand(10),
        love: 2 + rand(8),
      };
      all[slug] = seeded;
      writeMap(STORE_KEY, all);
    }
    setCounts(seeded);
    setMine((myAll as any)[slug] ?? null);
  }, [slug]);

  const react = (key: string) => {
    const all = readMap(STORE_KEY);
    const myAll = readMap(MINE_KEY) as unknown as Record<string, string>;
    const next: Counts = { ...(all[slug] ?? {}) };
    const prev = (myAll as any)[slug] as string | undefined;

    if (prev === key) {
      // Toggle off
      next[key] = Math.max(0, (next[key] ?? 1) - 1);
      delete (myAll as any)[slug];
      setMine(null);
    } else {
      if (prev) next[prev] = Math.max(0, (next[prev] ?? 1) - 1);
      next[key] = (next[key] ?? 0) + 1;
      (myAll as any)[slug] = key;
      setMine(key);
    }
    all[slug] = next;
    writeMap(STORE_KEY, all);
    writeMap(MINE_KEY, myAll as unknown as Record<string, Counts>);
    setCounts(next);
  };

  return (
    <section aria-label={ui("Reactions")}>
      <div className="mt-3 flex flex-wrap gap-2">

        {REACTIONS.map((r) => {
          const active = mine === r.key;
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => react(r.key)}
              aria-pressed={active}
              title={ui(r.label)}
              className={`plausible-event-name=Reaction+Click inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition ${
                active
                  ? "border-primary/60 bg-primary/10 text-primary"
                  : "border-border bg-background/40 text-foreground hover:border-primary/50 hover:text-primary"
              }`}
            >
              <span className="text-base leading-none">{r.emoji}</span>
              <span className="text-[11px] uppercase tracking-wider">{ui(r.label)}</span>
              <span className="ml-1 text-[11px] font-bold tabular-nums text-muted-foreground">
                {counts[r.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
